import type { ReactNode } from 'react';
import type { Insights } from '../../types/insights';
import { money } from '../labour/labourUtils';
import { num, pct } from './insightsUtils';

function Stat({ label, value, sub }: { label: string; value: ReactNode; sub?: ReactNode }) {
  return (
    <div className="border border-neutral-200 rounded-lg p-3 min-w-0">
      <p className="text-xs text-neutral-500">{label}</p>
      <p className="text-lg font-semibold text-neutral-900 truncate">{value}</p>
      {sub && <p className="text-xs text-neutral-500 truncate">{sub}</p>}
    </div>
  );
}

const th = 'text-right px-3 py-2 font-medium whitespace-nowrap';
const td = 'px-3 py-2 text-right whitespace-nowrap';

/** Season KPIs, then where the hours and money went by area, type of work and provider. */
export default function OverviewPanel({ data }: { data: Insights }) {
  const t = data.totals;
  const cost = !!data.includes_costs;
  const m = (n: number | null | undefined) => money(n, data.currency);

  return (
    <div className="space-y-6">
      <p className="text-sm text-neutral-500">
        {data.season_start} to {data.actuals_to}. Costs ex {data.tax_name}; tonnes are packed-out weight where a packout is entered, estimates elsewhere.
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        <Stat label="Hours" value={num(t.hours, 0)} sub={t.hours_per_ha != null ? `${num(t.hours_per_ha)} h/ha over ${num(t.area_hectares)} ha` : undefined} />
        {cost && <Stat label="Labour cost" value={m(t.cost)} sub={t.cost_per_ha != null ? `${m(t.cost_per_ha)}/ha` : undefined} />}
        {cost && <Stat label="Cost per tree" value={t.cost_per_tree != null ? m(t.cost_per_tree) : '—'} sub={t.tree_count ? `${t.tree_count.toLocaleString()} trees` : 'No tree counts'} />}
        <Stat label="Bins" value={num(t.bins)} sub={t.bins_per_ha != null ? `${num(t.bins_per_ha)} bins/ha` : undefined} />
        <Stat label="Tonnes" value={num(t.tonnes, 2)} sub={t.tonnes_per_ha != null ? `${num(t.tonnes_per_ha)} t/ha` : undefined} />
        <Stat label="Bins per picking hour" value={num(t.bins_per_picking_hour, 2)} sub={`${num(t.picking_hours, 0)} picking hours`} />
        {cost && <Stat label="Cost per bin" value={t.cost_per_bin != null ? m(t.cost_per_bin) : '—'} sub={t.cost_per_tonne != null ? `${m(t.cost_per_tonne)}/t` : undefined} />}
        {t.unrated_hours > 0 && cost && <Stat label="Hours with no rate" value={num(t.unrated_hours)} sub="Not in the costs" />}
      </div>

      <section className="space-y-2">
        <h3 className="font-semibold text-neutral-900">By cultivar area</h3>
        {data.by_cultivar_area.length === 0 ? <p className="text-sm text-neutral-500">No work recorded this season.</p> : (
          <div className="overflow-x-auto border border-neutral-200 rounded-lg">
            <table className="min-w-full text-sm">
              <thead className="bg-neutral-50 text-neutral-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Area</th>
                  <th className={th}>Hours</th><th className={th}>h/ha</th>
                  {cost && <><th className={th}>Cost</th><th className={th}>$/ha</th></>}
                  <th className={th}>Bins</th><th className={th}>t/ha</th>
                  {cost && <><th className={th}>$/bin</th><th className={th}>$/t</th></>}
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {data.by_cultivar_area.map(r => (
                  <tr key={r.cultivar_area_id ?? 'none'}>
                    <td className="px-3 py-2 min-w-[12rem]">
                      <p className="text-neutral-900">{r.name}</p>
                      {(r.block_name || r.cultivar_name) && <p className="text-xs text-neutral-500">{[r.block_name, r.cultivar_name].filter(Boolean).join(' · ')}</p>}
                    </td>
                    <td className={td}>{num(r.hours)}</td><td className={td}>{num(r.hours_per_ha)}</td>
                    {cost && <><td className={td}>{m(r.cost)}</td><td className={td}>{r.cost_per_ha != null ? m(r.cost_per_ha) : '—'}</td></>}
                    <td className={td}>{num(r.bins)}</td><td className={td}>{num(r.tonnes_per_ha)}</td>
                    {cost && <><td className={td}>{r.cost_per_bin != null ? m(r.cost_per_bin) : '—'}</td><td className={td}>{r.cost_per_tonne != null ? m(r.cost_per_tonne) : '—'}</td></>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className="space-y-2 min-w-0">
          <h3 className="font-semibold text-neutral-900">By type of work</h3>
          <div className="overflow-x-auto border border-neutral-200 rounded-lg">
            <table className="min-w-full text-sm">
              <thead className="bg-neutral-50 text-neutral-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Work</th>
                  <th className={th}>Hours</th><th className={th}>h/ha</th>
                  {cost && <><th className={th}>Cost</th><th className={th}>$/ha</th><th className={th}>$/tree</th></>}
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {data.by_activity.map(r => (
                  <tr key={r.activity_type_id ?? 'none'}>
                    <td className="px-3 py-2 text-neutral-900 whitespace-nowrap">{r.name}</td>
                    <td className={td}>{num(r.hours)}</td><td className={td}>{num(r.hours_per_ha)}</td>
                    {cost && <><td className={td}>{m(r.cost)}</td><td className={td}>{r.cost_per_ha != null ? m(r.cost_per_ha) : '—'}</td>
                      <td className={td}>{r.cost_per_tree != null ? m(r.cost_per_tree) : '—'}</td></>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
        <section className="space-y-2 min-w-0">
          <h3 className="font-semibold text-neutral-900">By labour provider</h3>
          <div className="overflow-x-auto border border-neutral-200 rounded-lg">
            <table className="min-w-full text-sm">
              <thead className="bg-neutral-50 text-neutral-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Provider</th>
                  <th className={th}>People</th><th className={th}>Hours</th><th className={th}>Bins</th>
                  <th className={th}>Bins/h</th><th className={th}>Rejects</th>
                  {cost && <th className={th}>$/bin</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {data.by_provider.map(r => (
                  <tr key={r.labour_provider_id ?? 'own'}>
                    <td className="px-3 py-2 text-neutral-900 whitespace-nowrap">{r.name}</td>
                    <td className={td}>{r.people}</td><td className={td}>{num(r.hours)}</td><td className={td}>{num(r.bins)}</td>
                    <td className={td}>{num(r.bins_per_picking_hour, 2)}</td>
                    <td className={td} title={r.packed_kg ? `${num(r.packed_kg, 0)} kg packed out` : 'No packouts yet'}>{pct(r.reject_pct)}</td>
                    {cost && <td className={td}>{r.cost_per_bin != null ? m(r.cost_per_bin) : '—'}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-neutral-500">Rejects: waste-grade share of packed-out kg from bins each provider’s pickers filled.</p>
        </section>
      </div>
    </div>
  );
}
